import React from 'react';

import clsx from 'clsx';
import styles from './IconButton.module.scss';

interface IIconButtonProps {
  /**
   * What a screen reader announces. Required, and deliberately so: these
   * controls contain an icon and no text, so without it they are announced as
   * just "button" and a non-sighted user has no way to tell the previous-page
   * arrow from the next-page one.
   */
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  /** The caller's own class, e.g. `styles.arrowContainer`. */
  className?: string;
  disabled?: boolean;
  title?: string;
}

/**
 * An icon-only control that is actually a button.
 *
 * Replaces the `<div className={styles.arrowContainer} onClick={...}>` pattern
 * that appeared 18 times across the console -- every paginator and every back
 * arrow. None of them were focusable, so paging through the finance, user,
 * doctor, article or notification lists required a mouse.
 *
 * The visual class stays with the caller and only the UA reset lives here, so
 * this composes with whatever each page already had rather than imposing a
 * look. `.reset` sets no `margin`, which is the one property every caller's
 * class does set.
 */
export const IconButton = ({
  label,
  onClick,
  children,
  className,
  disabled,
  title,
}: IIconButtonProps) => (
  <button
    // Explicit, because a button inside a <form> defaults to type="submit".
    type="button"
    aria-label={label}
    title={title}
    disabled={disabled}
    className={clsx(styles.reset, className)}
    onClick={onClick}
  >
    {children}
  </button>
);
